import React, { useState, useEffect, useRef } from 'react';
import './HamburgerMenu.css';
import { useAuth } from '../AuthContext';
import { useAuthModal } from '../AuthModalContext';
import { useCart } from '../CartContext';

const NAV_ITEMS = [
  { label: 'HOME',             key: 'HOME',            icon: 'home.png' },
  { label: 'UPCOMING EVENTS', key: 'UPCOMING EVENTS', icon: 'background-removed-background-removed.png', isNew: true },
  { label: 'TOURNAMENTS',     key: 'TOURNAMENTS',     icon: 'background-removed-background-removed-2.png' },
  { label: 'DROP INS',        key: 'DROP INS',         icon: 'hf_20260512_173251_0e58e0ae-cbf1-4f14-8707-09dce0580fcf-removebg-preview.png' },
  { label: 'GALLERY',         key: 'GALLERY',         icon: 'background-removed-background-removed-3.png' },
  { label: 'ABOUT US',        key: 'ABOUT US',        icon: 'hf_20260512_172122_89848c9e-dec9-4420-bb5b-5097bb866734-Photoroom.png' },
  { label: 'SPONSORS',        key: 'SPONSORS',        icon: 'background-removed-background-removed-4.png' },
  { label: 'OVR+ & MERCH',   key: 'OVR+',            icon: 'background-removed-background-removed-5.png', badge: 'COMING SOON' },
];

export default function HamburgerMenu({ onNavigate, onOpen, onSignOut, isAdmin, hideCart }) {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { setOpen: setCartOpen, count: cartCount } = useCart();
  const accountRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleNav = (key, comingSoon) => {
    if (comingSoon) return;
    setOpen(false);
    setTimeout(() => onNavigate(key), 200);
  };

  // Shared style for the round-square icon buttons in the top-right cluster
  const clusterBtn = (active) => ({
    position: 'relative',
    width: 44, height: 44, padding: 0,
    background: active ? 'rgba(157,21,21,0.18)' : 'rgba(8,8,8,0.75)',
    border: `1px solid ${active ? 'rgba(157,21,21,0.6)' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: 10, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    transition: 'all 0.2s ease',
  });
  const clusterBadge = {
    position: 'absolute', top: -5, right: -5,
    background: '#9D1515', color: '#fff',
    fontFamily: 'Inter, sans-serif', fontSize: 9, fontWeight: 700,
    minWidth: 16, height: 16, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
  };

  return (
    <>
      {/* Top-right action cluster: profile · cart · burger (single flex row — never overlaps) */}
      <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 1300, display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Profile (mobile cluster — desktop uses its own header profile) */}
        <button
          className="hbg-cluster-profile"
          aria-label={user ? 'My Events' : 'Sign in'}
          onClick={() => { if (!user) { openAuthModal(null, 'signup'); } else { onNavigate('MY EVENTS'); } }}
          style={clusterBtn(!!user)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={user ? '#9D1515' : '#ffffff'} strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4"/>
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
          </svg>
        </button>

        {/* Cart */}
        {!hideCart && (
        <button
          aria-label="Open cart"
          onClick={() => setCartOpen(true)}
          style={clusterBtn(cartCount > 0)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={cartCount > 0 ? '#9D1515' : '#ffffff'} strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 1.99 1.61h9.72a2 2 0 0 0 1.99-1.61L23 6H6"/>
          </svg>
          {cartCount > 0 && <span style={clusterBadge}>{cartCount}</span>}
        </button>
        )}

        {/* Hamburger */}
        <button
          className={`hbg-btn${open ? ' hbg-btn--open' : ''}`}
          onClick={() => setOpen(o => { if (!o) { setCartOpen(false); if (onOpen) onOpen(); } return !o; })}
          aria-label={open ? 'Close menu' : 'Open menu'}
          style={{ position: 'static' }}
        >
          <span className="hbg-line" />
          <span className="hbg-line" />
          <span className="hbg-line" />
        </button>
      </div>

      {/* Backdrop */}
      <div className={`hbg-backdrop${open ? ' hbg-backdrop--visible' : ''}`} onClick={() => setOpen(false)} />

      {/* Panel */}
      <nav className={`hbg-panel${open ? ' hbg-panel--open' : ''}`} aria-hidden={!open || undefined} {...(!open ? { inert: '' } : {})}>
        {/* Panel header */}
        <div className="hbg-header">
          <div onClick={() => handleNav('HOME')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <img
              src={'/ovr-logo.png'}
              alt="OVR"
              className="hbg-header__logo"
            />
            <span className="hbg-header__label">NAVIGATE</span>
          </div>

          {/* Mobile-only quick actions */}
          <div className="hbg-quick-actions">
            {/* Profile / account */}
            <button
              className="hbg-quick-btn"
              aria-label={user ? 'My Events' : 'Sign in'}
              onClick={() => {
                setOpen(false);
                if (!user) { setTimeout(() => openAuthModal(null, 'signup'), 250); }
                else { setTimeout(() => onNavigate('MY EVENTS'), 200); }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke={user ? '#9D1515' : '#fff'} strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
            </button>

            {/* Cart */}
            {!hideCart && (
            <button
              className="hbg-quick-btn"
              aria-label="Open cart"
              onClick={() => { setOpen(false); setCartOpen(true); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke={cartCount > 0 ? '#9D1515' : '#fff'} strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.61L23 6H6"/>
              </svg>
              {cartCount > 0 && <span className="hbg-quick-badge">{cartCount}</span>}
            </button>
            )}
          </div>
        </div>

        {/* Nav items */}
        <ul className="hbg-list">
          {NAV_ITEMS.map((item, i) => (
            <li key={item.key} className="hbg-item" style={{ animationDelay: open ? `${i * 55}ms` : '0ms' }}>
              <button
                className={`hbg-link${item.comingSoon ? ' hbg-link--disabled' : ''}`}
                onClick={() => handleNav(item.key, item.comingSoon)}
                disabled={item.comingSoon}
              >
                {item.icon
                  ? <img src={`/${item.icon}`} alt="" className="hbg-link__icon" />
                  : item.key === 'HOME'
                    ? <span className="hbg-link__number" style={{ fontSize: 18 }}>⌂</span>
                    : <span className="hbg-link__number">0{i}</span>
                }
                <span className="hbg-link__text">{item.label}</span>
                {item.comingSoon && <span className="hbg-coming-soon">COMING SOON</span>}
                {item.badge && <span className="hbg-coming-soon">{item.badge}</span>}
                {item.isNew && !item.comingSoon && <span className="hbg-new-badge">NEW</span>}
                {!item.comingSoon && <span className="hbg-link__arrow">→</span>}
              </button>
            </li>
          ))}
        </ul>

        {/* Account */}
        <div className="hbg-account" ref={accountRef}>
          {user ? (
            <div className="hbg-account__info">
              <div className="hbg-account__identity">
                <div className="hbg-account__welcome">WELCOME BACK</div>
                <div className="hbg-account__name">{(user.user_metadata?.full_name || (user.email ? user.email.split('@')[0] : 'PLAYER')).toUpperCase()}</div>
                <div className="hbg-account__email">{user.email || ''}</div>
              </div>
              <div className="hbg-account__actions">
                <button className="hbg-account__myevents" onClick={() => { setOpen(false); setTimeout(() => onNavigate('MY EVENTS'), 200); }}>MY EVENTS &amp; ORDERS →</button>
                {isAdmin && (
                  <button className="hbg-account__admin" onClick={() => { setOpen(false); setTimeout(() => onNavigate('ADMIN'), 200); }}>ADMIN PANEL →</button>
                )}
                <button className="hbg-account__signout" onClick={() => {
                  setOpen(false);
                  if (onSignOut) { onSignOut(); } else { signOut().then(() => { window.location.href = '/'; }); }
                }}>SIGN OUT</button>
              </div>
            </div>
          ) : (
            <button className="hbg-account__signin" onClick={() => { setOpen(false); openAuthModal(null, 'signup'); }}>
              SIGN IN / CREATE ACCOUNT →
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="hbg-footer">
          <a href="https://www.instagram.com/ovrleague" target="_blank" rel="noopener noreferrer" className="hbg-social">@ovrleague</a>
          <span className="hbg-footer__sep">•</span>
          <a href="https://www.instagram.com/horizoneventsofficial" target="_blank" rel="noopener noreferrer" className="hbg-social">@horizoneventsofficial</a>
        </div>
      </nav>
    </>
  );
}
